import { ActionIcon, Box, Group, LoadingOverlay, Tooltip } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import {
    IconFiles,
    IconPlus,
    IconSquareArrowRight,
    IconTablePlus,
    IconTrash,
    IconVocabulary,
} from '@tabler/icons-react';
import type { ComponentProps, MouseEvent } from 'react';
import type { OverrideProperties } from 'type-fest';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { MantineTable } from '../../../../../react-commons/components/table/MantineTable';
import type { ResourceType } from '../../../resource/enumerations/ResourceType';
import type { DetailedValidationResource } from '../../../resource/resources/validation/detailedValidationResource';
import { TOOLTIPS } from '../../../resource/tooltipTexts';
import { EditDeleteButtons } from '../base/EditDeleteButtons';
import { ResourceDetailsWithMultiValidationButton } from '../validation/ResourceDetailsWithMultiValidationButton';

type CrudValidationTableProps<R extends object> = {
    resourceType: ResourceType | Array<ResourceType>;
    onAdd: (() => void) | undefined;
    onAddDisabled?: boolean;
    onBatchAdd?: (event: MouseEvent<HTMLButtonElement>) => void;
    onBatchDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
    onEdit: ((resource: R, index: number) => void) | undefined;
    onDelete: ((resource: R, index: number) => void) | undefined;
    onIndexChange?: (resource: R, oldIndex: number, newIndex: number) => void;
    onClone?: (resource: R, index: number) => void;
    onGoToReference?: (resource: R, index: number) => void;
    onOverview?: (resource: R, index: number) => void;
    batchDeleteEnabled?: boolean;
    openWithoutValidation?: boolean;
    readonly?: boolean;
} & (
    | {
          validationData: undefined;
          getIdForValidation?: never;
          validationIsLoading?: never;
      }
    | {
          validationData: Record<string, DetailedValidationResource> | null | undefined;
          getIdForValidation: (resource: R) => string;
          validationIsLoading: boolean;
      }
) &
    OverrideProperties<
        Omit<ComponentProps<typeof MantineTable<R, true>>, 'enableActions' | 'rowActionsWidth' | 'rowActionsHeader'>,
        { tableOptions?: Omit<ComponentProps<typeof MantineTable<R, true>>['tableOptions'], 'renderRowActions'> }
    >;

export const OverviewCrudValidationTable = <R extends object>({
    resourceType,
    validationData,
    getIdForValidation,
    validationIsLoading,
    batchDeleteEnabled,
    onAdd,
    onAddDisabled,
    onBatchAdd,
    onBatchDelete,
    onEdit,
    onDelete,
    onIndexChange,
    onClone,
    onGoToReference,
    onOverview,
    tableOptions,
    openWithoutValidation,
    readonly,
    ...props
}: CrudValidationTableProps<R>) => {
    const rowActionsWidth =
        [onEdit, onDelete, getIdForValidation, onClone, onGoToReference, onOverview].filter(Boolean).length * 28;
    const rowDraggingEnabled = onIndexChange?.let((it) => Boolean(it));
    const rowDraggingDisabled = rowDraggingEnabled?.let((it) => !it);
    const { ref, width: measuredWidth } = useElementSize();
    const width = Math.max(rowActionsWidth, measuredWidth);
    return (
        <MantineTable
            {...props}
            tableOptions={{
                ...tableOptions,
                renderRowActions: ({ row: { original: resource, index } }) => (
                    <Group wrap={'nowrap'} gap={6} w={width} justify={'end'}>
                        <Show when={onClone}>
                            {(onC) => (
                                <Tooltip label={'Clone'}>
                                    <ActionIcon
                                        variant={'transparent'}
                                        color={'var(--mantine-text-secondary-color)'}
                                        onClick={() => onC(resource, index)}
                                        size={'sm'}
                                    >
                                        <IconFiles />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Show>
                        <Show when={onGoToReference}>
                            {(onG) => (
                                <Tooltip label={TOOLTIPS.GO_TO_REFERENCE}>
                                    <ActionIcon
                                        variant={'transparent'}
                                        color={'var(--mantine-text-secondary-color)'}
                                        onClick={() => onG(resource, index)}
                                        size={'sm'}
                                    >
                                        <IconSquareArrowRight />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Show>
                        <Show when={onOverview}>
                            {(onO) => (
                                <Tooltip label={'Overview'}>
                                    <ActionIcon
                                        variant={'transparent'}
                                        color={'var(--mantine-text-secondary-color)'}
                                        onClick={() => onO(resource, index)}
                                        size={'sm'}
                                    >
                                        <IconVocabulary />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Show>
                        <EditDeleteButtons
                            handleEdit={onEdit ? () => onEdit(resource, index) : undefined}
                            handleDelete={onDelete ? () => onDelete(resource, index) : undefined}
                            readonly={readonly}
                        />
                        {getIdForValidation && (
                            <ResourceDetailsWithMultiValidationButton
                                resourceValidationResults={validationData?.[getIdForValidation(resource)]?.let((it) => [
                                    it,
                                ])}
                                resourceType={resourceType}
                                disabled={validationIsLoading || !validationData}
                            />
                        )}
                    </Group>
                ),
                enableRowOrdering: rowDraggingEnabled,
                autoResetPageIndex: rowDraggingDisabled,
                enableSorting: rowDraggingDisabled,
                mantineRowDragHandleProps:
                    rowDraggingEnabled ?
                        (props) => ({
                            onDragEnd: () => {
                                if (!onIndexChange) {
                                    return;
                                }
                                const { draggingRow, hoveredRow } = props.table.getState();
                                const resource = draggingRow?.original;
                                const originalIndex = draggingRow?.index;
                                const newIndex = hoveredRow?.index;

                                if (resource && originalIndex !== undefined && newIndex !== undefined) {
                                    onIndexChange(resource, originalIndex, newIndex);
                                }
                            },
                        })
                    :   undefined,
            }}
            enableActions={true}
            rowActionsWidth={width}
            rowActionsHeader={
                <Group ref={ref} wrap={'nowrap'} gap={6} justify={'end'}>
                    <Show when={onAdd}>
                        <Tooltip label={'Add'}>
                            <ActionIcon
                                variant={'transparent'}
                                color={'var(--mantine-text-secondary-color)'}
                                onClick={onAdd}
                                size={'sm'}
                                disabled={onAddDisabled === true || readonly}
                            >
                                <IconPlus />
                            </ActionIcon>
                        </Tooltip>
                    </Show>
                    <Show when={onBatchAdd}>
                        {(onBA) => (
                            <Tooltip label={'Batch Add'}>
                                <ActionIcon
                                    variant={'transparent'}
                                    color={'var(--mantine-text-secondary-color)'}
                                    onClick={onBA}
                                    size={'sm'}
                                    disabled={readonly}
                                >
                                    <IconTablePlus />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Show>
                    <Show when={onBatchDelete}>
                        {(onBD) => (
                            <Tooltip label={'Bulk Delete'}>
                                <ActionIcon
                                    disabled={!batchDeleteEnabled}
                                    variant={'transparent'}
                                    color={'var(--mantine-text-secondary-color)'}
                                    onClick={onBD}
                                    size={'sm'}
                                >
                                    <IconTrash />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Show>
                    <Box pos="relative" h={22}>
                        <LoadingOverlay
                            visible={validationIsLoading}
                            loaderProps={{
                                size: 'xs',
                                style: {
                                    position: 'absolute',
                                    top: 2,
                                    left: 2,
                                },
                            }}
                        />
                        <ResourceDetailsWithMultiValidationButton
                            resourceValidationResults={validationData?.let((it) => Object.values(it)).filter(Boolean)}
                            resourceType={resourceType}
                            openWithoutValidation={openWithoutValidation}
                        />
                    </Box>
                </Group>
            }
        />
    );
};
